import { QuoteLocation } from 'api/accessexpress/schema';
import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { FC, useEffect, useRef, useState } from 'react';
import { animate, useMotionValue, useMotionValueEvent } from 'framer-motion';
import clsx from 'clsx';
import { SearchAddressBar } from 'screens/AccessExpress/BaseComponents/SearchAddressBar/SearchAddressBar';
import LinearDashedBorder from 'screens/ThemeDesigner/logo/LinearDashedBorder';
import { getLocationsForWL } from 'api/accessexpress/api';

export interface SearchProps {
  addressValue?: QuoteLocation & { street?: string | null | undefined };
  setAddressValue: React.Dispatch<
    React.SetStateAction<SearchProps['addressValue'] | undefined>
  >;
  onLoadingDone: () => void;
}

const Search: FC<SearchProps> = ({
  addressValue,
  setAddressValue,
  onLoadingDone,
}) => {
  const searchBarRef = useRef<{ focusInput: () => void }>(null);
  const [showLoading, setShowLoading] = useState(false);

  const handleSearchClick = async () => {
    if (!addressValue) return;
    setShowLoading((prev) => !prev);
  };

  if (showLoading)
    return (
      <Loading
        onDone={() => {
          onLoadingDone();
          setShowLoading(false);
        }}
      />
    );

  return (
    <>
      <div
        className={clsx(
          'w-full self-center rounded bg-background-base-surface-2-subdued p-6 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.3),0px_8px_12px_6px_rgba(0,0,0,0.15)] md:max-w-[800px] lg:w-[800px]',
        )}
      >
        <div className="flex flex-col gap-2 md:flex-row">
          <SearchAddressBar
            ref={searchBarRef}
            onAddressSelected={setAddressValue}
            className="w-full flex-1 md:w-[80%] [&_.search-bar-label]:overflow-hidden"
            value={addressValue}
            placeholder="Search address..."
            getSearchLocations={getLocationsForWL}
          />
          <GraniteButton
            size="large"
            onClick={handleSearchClick}
            className={clsx('max-h-12')}
          >
            Search
          </GraniteButton>
        </div>
      </div>
    </>
  );
};

export default Search;

const Loading = ({ onDone }: { onDone?: () => void }) => {
  const [percent, setPercent] = useState(10);
  const mv = useMotionValue(10);

  useMotionValueEvent(mv, 'change', (v) => setPercent(v));

  useEffect(() => {
    const controls = animate(mv, 100, {
      duration: 0.45,
      ease: 'easeInOut',
      delay: 0.4,
    });
    controls.then(() => {
      onDone?.();
    });
    return () => controls.stop();
  }, [mv, onDone]);

  return (
    <div className="relative w-full max-w-[800px] self-center overflow-hidden rounded bg-[linear-gradient(97.76deg,_rgba(126,_119,_179,_0.08)_3.17%,_rgba(137,_137,_255,_0.08)_26.59%,_rgba(108,_157,_255,_0.08)_49.93%,_rgba(120,_207,_218,_0.08)_73.45%,_rgba(159,_244,_169,_0.08)_96.88%)] px-4">
      <div className="flex flex-col items-center justify-center py-7 sm:px-8 md:px-[70px] ">
        <div className=" w-full rounded bg-neutral-500 bg-opacity-[0.08] p-4 shadow-[rgba(0,_0,_0,_0.16)]">
          <div
            className={clsx(
              'h-2.5 rounded-sm shadow-elevation1',
              'bg-green-300',
            )}
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>
      <LinearDashedBorder width={800} height={72} />
    </div>
  );
};
